import React from 'react'
import { ClientOnly, createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircleIcon, SparklesIcon } from 'lucide-react'

import { useConfig } from '@/components/hooks/use-config'
import { useResumeTailor } from '@/components/hooks/use-resume-tailor'
import { UploadStage } from '@/components/resume-tailor/1-upload-stage'
import { AnalysisStage } from '@/components/resume-tailor/2-analysis-stage'
import { ResultStage } from '@/components/resume-tailor/3-result-stage'
import { ControlBar } from '@/components/resume-tailor/control-bar'
import { Stepper } from '@/components/resume-tailor/stepper'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

export const Route = createFileRoute('/_index/')({
  component: () => (
    <ClientOnly>
      <RouteComponent />
    </ClientOnly>
  ),
})

function RouteComponent() {
  const { isConfigured } = useConfig()

  const {
    state: { selectedFiles, data, step, isStreaming, isAnalyzing, isTailoring },
    actions: {
      setSelectedFiles,
      setData,
      handleAnalyze,
      handleTailor,
      handleFinish,
    },
  } = useResumeTailor()

  React.useEffect(() => {
    if (isStreaming) {
      const elem = document.getElementById('resume-stream-cursor')
      elem?.scrollIntoView({
        behavior: 'instant',
        block: 'center',
        inline: 'nearest',
      })
    }
  }, [
    isStreaming,
    data.analysis?.ats_score,
    data.analysis?.reasonings,
    data.analysis?.improvements,
  ])

  if (!isConfigured) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-6 py-16">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <AlertCircleIcon className="h-7 w-7 text-muted-foreground" />
            </div>
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-semibold tracking-tight">
                Configuration Required
              </h2>
              <p className="max-w-md text-muted-foreground">
                Please configure your LLM provider to use the resume tailoring
                features.
              </p>
            </div>
            <Link to="/settings">
              <Button size="lg" className="cursor-pointer gap-2">
                <SparklesIcon className="h-4 w-4" />
                Configure LLM
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="mx-auto mb-25 max-w-6xl">
        <div className="mb-10 text-center">
          <h1 className="mb-2 text-3xl font-bold tracking-tight">
            Tailor Your Resume
          </h1>
          <p className="text-muted-foreground">
            Upload your resume and job description to get an optimized,
            ATS-friendly version
          </p>
        </div>
        <Stepper step={step} />

        {step === 'upload' && (
          <UploadStage
            {...{
              selectedFiles,
              setSelectedFiles,
              data,
              setData,
              isMovingToNextStage: isAnalyzing,
              onNextStep: () =>
                handleAnalyze(data.input.resume, data.input.job_description),
            }}
          />
        )}

        {(step === 'analyze' || step === 'tailor') && (
          <AnalysisStage
            {...{
              data,
              setData,
              isStreaming,
              isMovingToNextStage: isTailoring,
              onNextStep: () => handleTailor(),
            }}
          />
        )}

        {step === 'result' && data?.result && (
          <ResultStage
            {...{
              data,
              setData,
              isStreaming,
              isAnalyzing,
              onFinished: handleFinish,
            }}
          />
        )}
      </div>
      {(isAnalyzing || ['analyze', 'result'].includes(step)) && (
        <ControlBar {...{ data, step }} />
      )}
    </React.Fragment>
  )
}
